/* eslint-disable unicorn/no-null */
import { useMutation, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { CustomerAPI, TCustomerSessionReportParams } from '@newo/api/gateway';
import type {
    TAttributeData,
    TCustomerStatus,
    TAccountLinkData,
    TAccountLinkParams,
    TCustomerSetting
} from '@newo/api/gateway';
import axios from '@/utils/api/axiosInstance';

const customerAPI = new CustomerAPI(axios);
const HTTP_NOT_FOUND = 404;

// GET Customer Attribute
export const useCustomerAttributeQuery = (attributeIdn: string) =>
    useQuery({
        queryKey: ['customer/attribute', attributeIdn],
        queryFn: async () =>
            customerAPI.getCustomerAttribute(attributeIdn, {
                noErrorMessage: true
            })
    });

// CREATE Customer Attribute
export const useCustomerCreateAttributeMutation = () =>
    useMutation({
        mutationFn: async (data: TAttributeData) =>
            customerAPI.createCustomerAttribute(data)
    });

// UPDATE Customer Attribute
export const useCustomerUpdateAttributeMutation = () =>
    useMutation({
        mutationFn: async ({
            attributeId,
            data
        }: {
            attributeId: string;
            data: TAttributeData;
        }) => customerAPI.updateCustomerAttribute(attributeId, data)
    });

// DELETE Customer Attribute
export const useCustomerDeleteAttributeMutation = () =>
    useMutation({
        mutationFn: async ({ attributeId }: { attributeId: string }) =>
            customerAPI.deleteCustomerAttribute(attributeId)
    });

// DELETE All Customer Attributes
export const useCustomerDeleteAllAttributesMutation = () =>
    useMutation({
        mutationFn: async () => customerAPI.deleteAllCustomerAttributes()
    });

// GET Customer Setting
export const useCustomerSettingQuery = (settingIdn: string) =>
    useQuery({
        queryKey: ['customer/setting', settingIdn],
        queryFn: async (): Promise<TCustomerSetting | null> => {
            try {
                return await customerAPI.getCustomerSetting(settingIdn, {
                    noErrorMessage: true
                });
            } catch (error) {
                if (
                    isAxiosError(error) &&
                    error.response?.status === HTTP_NOT_FOUND
                ) {
                    return null;
                }
                throw error;
            }
        },
        staleTime: Number.POSITIVE_INFINITY,
        refetchOnWindowFocus: false,
        retry: false
    });

// SET Customer status
export const useCustomerSetStatusMutation = () =>
    useMutation({
        mutationFn: async ({
            customerId,
            data
        }: {
            customerId: string;
            data: { status: TCustomerStatus };
        }) => customerAPI.setCustomerStatus(customerId, data)
    });

// Get customer account links
export const useCustomerAccountLinksQuery = (params?: TAccountLinkParams) =>
    useQuery({
        queryKey: ['customer/account-links', params],
        queryFn: async () => customerAPI.getCustomerAccountLinks(params)
    });

// Create customer account link
export const useCreateAccountLinkMutation = () =>
    useMutation({
        mutationFn: async ({ data }: { data: TAccountLinkData }) =>
            customerAPI.createCustomerAccountLink(data)
    });

// Update customer account link
export const useUpdateAccountLinkMutation = () =>
    useMutation({
        mutationFn: async ({
            accountId,
            data
        }: {
            accountId: string;
            data: TAccountLinkData;
        }) => customerAPI.updateCustomerAccountLink(accountId, data)
    });

// Delete customer account link
export const useDeleteAccountLinkMutation = () =>
    useMutation({
        mutationFn: async ({ accountId }: { accountId: string }) =>
            customerAPI.deleteCustomerAccountLink(accountId)
    });

export const useMyAccountLinkQuery = (isEnabled: boolean) =>
    useQuery({
        enabled: isEnabled,
        queryKey: ['customer/account-links/me'],
        queryFn: customerAPI.getMyAccountLink
    });

// Send customer session list report to email
export const useSendSessionReportToEmailMutation = () =>
    useMutation({
        mutationFn: async ({ data }: { data: TCustomerSessionReportParams }) =>
            customerAPI.exportCustomerSessionReportToEmail(data)
    });
